import { createTheme, ThemeOptions } from '@mui/material/styles';

import { getPalette } from '@theme/palettes/getPalette';
import { themeBase } from '@theme/base';
import { getComponents } from '@theme/components';
import { getTypographyVariants } from '@theme/styleOverrides/typography';
import { FontSize, GridColumn, OfferingThemeMode, TLayoutPreferences } from '@store/types/UserStoreSlice.type';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { useLocation } from 'react-router-dom';
import { useMemo } from 'react';
import { ThemeMode } from '@theme/palettes/PaletteOptions.interface';
import { getMixins } from '@theme/mixins';

export const useComposeTheme = (
  initialMode: ThemeMode,
  options?: {
    // use to force a specific font size
    fontSizeOverride?: FontSize;
    themeOverride?: ThemeMode;
  },
) => {
  const storedLayoutPreferences = useStore((state) => state.layoutPreferences, shallow);
  const layoutPreferences = storedLayoutPreferences
    ? { ...storedLayoutPreferences }
    : {
        offeringColorMode: OfferingThemeMode.DEFAULT,
        offeringGridColumns: GridColumn.OFFERING_DEFAULT,
        fontSize: FontSize.DEFAULT,
      };

  const fontSizeOverride = options?.fontSizeOverride;

  if (fontSizeOverride) {
    layoutPreferences.fontSize = fontSizeOverride;
  }

  const isOnQuotesPage = useLocation().pathname.startsWith('/quote/') && !options?.themeOverride;

  const theme = useMemo(() => {
    // if on quote page, use the offering color mode else only ever use initial (for now)

    // FUTURE TODO: do not make specific to quotes page once we go to a wider use case

    const mode =
      isOnQuotesPage && layoutPreferences.offeringColorMode === OfferingThemeMode.DARK ? 'trueDark' : initialMode;
    const palette = getPalette(mode, FontSize.DEFAULT);
    const mixins = getMixins(palette as any);

    const fontSize = layoutPreferences?.fontSize ?? FontSize.DEFAULT;
    // creating the palette first allows us to use in our component default styling :)
    const components = getComponents(mode, palette as any, fontSize);
    const typography = getTypographyVariants(fontSize);

    const customTheme = createTheme({
      ...themeBase,
      mixins,
      typography,
      palette,
      components,
    } as ThemeOptions);

    return customTheme;
  }, [initialMode, isOnQuotesPage, layoutPreferences?.fontSize, layoutPreferences.offeringColorMode]);

  return theme;
};

/**
 *  @deprecated Use useComposeTheme instead
 */
const composeTheme = (mode: ThemeMode, layoutPreferences?: TLayoutPreferences) => {
  const palette = getPalette(mode, layoutPreferences?.fontSize ?? FontSize.DEFAULT);
  const mixins = getMixins(palette as any);

  const fontSize = layoutPreferences?.fontSize ?? FontSize.DEFAULT;
  // creating the palette first allows us to use in our component default styling :)
  const components = getComponents(mode, palette as any, fontSize);
  const typography = getTypographyVariants(fontSize);

  const customTheme = createTheme({
    ...themeBase,
    mixins,
    typography,
    palette,
    components,
  } as ThemeOptions);

  return customTheme;
};

export default composeTheme;
